/* Sakeena London – small progressive enhancements. No dependencies, deferred. */
(() => {
  /* Keyboard-only focus rings */
  const root = document.documentElement;
  document.addEventListener('keydown', (e) => { if (e.key === 'Tab') root.classList.add('sl-kbd'); });
  ['mousedown', 'touchstart', 'pointerdown'].forEach((ev) => document.addEventListener(ev, () => root.classList.remove('sl-kbd'), { passive: true }));

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Dialogs (size guide) ---------- */
  let lastTrigger = null;
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-sl-dialog-open]');
    if (open) {
      const dlg = document.getElementById(open.dataset.slDialogOpen);
      if (dlg && typeof dlg.showModal === 'function') {
        e.preventDefault();
        lastTrigger = open;
        dlg.showModal();
        document.documentElement.classList.add('sl-dialog-open');
        if (root.classList.contains('sl-kbd')) dlg.querySelector('[data-sl-dialog-close]')?.focus();
      }
      return;
    }
    const close = e.target.closest('[data-sl-dialog-close]');
    if (close) { close.closest('dialog')?.close(); return; }
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('sl-dialog')) e.target.close(); // backdrop
  });
  document.addEventListener('close', (e) => {
    if (e.target.classList?.contains('sl-dialog')) {
      document.documentElement.classList.remove('sl-dialog-open');
      if (root.classList.contains('sl-kbd')) lastTrigger?.focus(); else document.activeElement?.blur?.();
    }
  }, true);

  /* ---------- Size chart units ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-sl-unit]');
    if (!btn) return;
    const wrap = btn.closest('[data-sl-size]');
    const unit = btn.dataset.slUnit;
    wrap.querySelectorAll('[data-sl-unit]').forEach((b) => {
      const on = b === btn; b.classList.toggle('is-active', on); b.setAttribute('aria-pressed', on);
    });
    wrap.querySelectorAll('td[data-in]').forEach((td) => {
      const v = parseFloat(td.dataset.in);
      td.textContent = unit === 'cm' ? (Math.round(v * 2.54 * 2) / 2).toString() : td.dataset.in;
    });
    const lbl = wrap.querySelector('[data-sl-unit-label]');
    if (lbl) lbl.textContent = unit === 'cm' ? 'centimetres' : 'inches';
  });

  /* ---------- Read more ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-sl-more-toggle]');
    if (!t) return;
    const box = document.getElementById(t.getAttribute('aria-controls'));
    const expanded = t.getAttribute('aria-expanded') === 'true';
    t.setAttribute('aria-expanded', String(!expanded));
    box.dataset.collapsed = expanded ? 'true' : 'false';
    t.querySelector('[data-open-label]').hidden = !expanded;
    t.querySelector('[data-close-label]').hidden = expanded;
  });

  /* ---------- Table of contents for long pages ---------- */
  document.querySelectorAll('[data-sl-doc]').forEach((doc) => {
    const toc = doc.closest('.sl-doc__grid')?.querySelector('[data-sl-toc]');
    if (!toc) return;
    const heads = [...doc.querySelectorAll('h2')].filter((h) => !h.closest('.sl-doc__help'));
    if (heads.length < 3) return;
    const list = toc.querySelector('[data-sl-toc-list]');
    heads.forEach((h, i) => {
      if (!h.id) h.id = 'section-' + (h.textContent.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || i);
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = '#' + h.id; a.textContent = h.textContent.trim();
      li.appendChild(a); list.appendChild(li);
    });
    toc.hidden = false;
    if ('IntersectionObserver' in window) {
      const links = list.querySelectorAll('a');
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) links.forEach((l) => l.toggleAttribute('aria-current', l.hash === '#' + en.target.id));
        });
      }, { rootMargin: '0px 0px -70% 0px' });
      heads.forEach((h) => io.observe(h));
    }
  });

  /* ---------- Track order ---------- */
  document.querySelectorAll('[data-sl-track]').forEach((form) => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const num = form.number.value.trim().replace(/\s+/g, '');
      const err = form.querySelector('.sl-field__error');
      if (!num) { err.hidden = false; form.number.setAttribute('aria-invalid', 'true'); form.number.focus(); return; }
      err.hidden = true; form.number.removeAttribute('aria-invalid');
      const n = encodeURIComponent(num);
      const url = `https://t.17track.net/en#nums=${n}`;
      window.open(url, '_blank', 'noopener');
    });
  });



  /* ---------- Custom measurements ---------- */
  document.querySelectorAll('[data-sl-meas]').forEach((box) => {
    const inputs = [...box.querySelectorAll('.sl-meas__grid input, textarea')];
    const unitVal = box.querySelector('[data-sl-meas-unit-value]');
    const err = box.querySelector('[data-sl-meas-error]');
    const sync = () => {
      const any = inputs.some((i) => i.value.trim() !== '');
      if (unitVal) unitVal.disabled = !any;
      box.classList.toggle('has-values', any);
    };
    inputs.forEach((i) => i.addEventListener('input', () => { sync(); i.removeAttribute('aria-invalid'); if (err) err.hidden = true; }));
    box.querySelectorAll('[data-sl-meas-unit]').forEach((r) => r.addEventListener('change', () => {
      if (unitVal) unitVal.value = r.value;
      box.querySelectorAll('[data-sl-meas-suffix]').forEach((s) => { s.textContent = r.value === 'cm' ? 'cm' : 'in'; });
    }));
    const section = box.closest('.shopify-section') || document;
    const isCustom = () => {
      const checked = section.querySelector('variant-picker input[type="radio"]:checked, variant-picker select');
      const vals = [...section.querySelectorAll('variant-picker input[type="radio"]:checked')].map((x) => x.value.toLowerCase());
      section.querySelectorAll('variant-picker select').forEach((s) => vals.push(String(s.value).toLowerCase()));
      return vals.some((v) => v.includes('custom'));
    };
    const onVariant = () => { const c = isCustom(); box.classList.toggle('is-required', c); if (c && !box.open) box.open = true; };
    section.addEventListener('change', (e) => { if (e.target.closest('variant-picker')) setTimeout(onVariant, 30); });
    onVariant();
    const form = document.getElementById(box.querySelector('input[form]')?.getAttribute('form'));
    if (form) {
      form.addEventListener('submit', (e) => {
        if (!isCustom()) return;
        const missing = [...box.querySelectorAll('[data-sl-meas-req]')].filter((i) => !i.value.trim());
        if (missing.length) {
          e.preventDefault(); e.stopImmediatePropagation();
          box.open = true; if (err) err.hidden = false;
          missing.forEach((i) => i.setAttribute('aria-invalid', 'true'));
          missing[0].focus();
        }
      }, true);
    }
    sync();
  });

  /* ---------- Estimated delivery dates (live, en-GB) ---------- */
  document.querySelectorAll('[data-sl-eta]').forEach((el) => {
    const min = parseInt(el.dataset.min, 10) || 15, max = parseInt(el.dataset.max, 10) || 18;
    const fmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    const d = (n) => { const x = new Date(); let added = 0; while (added < n) { x.setDate(x.getDate() + 1); const w = x.getDay(); if (w !== 0 && w !== 6) added++; } return fmt.format(x); };
    const out = el.querySelector('[data-sl-eta-dates]');
    if (out) out.textContent = `${d(min)} – ${d(max)}`;
  });

  /* ---------- Horizontal card tracks (arrows) ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-sl-scroll]');
    if (!b) return;
    const track = b.closest('section')?.querySelector('[data-sl-track-scroll]');
    if (!track) return;
    const item = track.querySelector('li');
    const step = item ? item.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
    track.scrollBy({ left: step * parseInt(b.dataset.slScroll, 10) * 2, behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  /* ---------- Reveal on scroll (respects reduced motion) ---------- */
  const reveals = document.querySelectorAll('[data-sl-reveal]');
  if (reduceMotion || !('IntersectionObserver' in window)) {
    reveals.forEach((el) => el.classList.add('is-in'));
  } else {
    document.documentElement.classList.add('sl-motion');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    reveals.forEach((el) => io.observe(el));
  }


  /* ---------- Mobile drawer: size to the visible screen (iOS toolbars, sticky header) ---------- */
  const drawer = document.getElementById('m-menu-drawer');
  const hdr = document.querySelector('.m-header__mobile');
  if (drawer && hdr) {
    const fit = () => {
      if (!drawer.classList.contains('open')) return;
      const vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const top = Math.max(0, hdr.getBoundingClientRect().bottom);
      drawer.style.setProperty('--sl-drawer-h', Math.round(vh - top) + 'px');
    };
    new MutationObserver(() => { requestAnimationFrame(fit); setTimeout(fit, 260); }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    (window.visualViewport || window).addEventListener('resize', fit, { passive: true });
    window.addEventListener('orientationchange', () => setTimeout(fit, 300));
  }


  /* ---------- Wishlist heart: pop on save, count bump, accessible state ---------- */
  const syncWish = (btn) => {
    const on = btn.classList.contains('added-to-wishlist') || btn.classList.contains('m-wishlist-remove-button');
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    const name = (btn.getAttribute('aria-label') || '').replace(/^(Save|Remove) /, '').replace(/ (to|from) wishlist$/, '');
    if (name) btn.setAttribute('aria-label', (on ? 'Remove ' : 'Save ') + name + (on ? ' from wishlist' : ' to wishlist'));
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.sl-wish');
    if (!btn) return;
    setTimeout(() => {
      syncWish(btn);
      if (btn.classList.contains('added-to-wishlist')) {
        btn.classList.remove('sl-wish--pop'); void btn.offsetWidth; btn.classList.add('sl-wish--pop');
        setTimeout(() => btn.classList.remove('sl-wish--pop'), 700);
        document.querySelectorAll('.m-wishlist-count, .sl-mnav__item[href*="wishlist"] svg').forEach((el) => {
          el.classList.remove('sl-bump'); void el.offsetWidth; el.classList.add('sl-bump');
        });
      }
    }, 0);
  });
  const syncAll = () => document.querySelectorAll('.sl-wish').forEach(syncWish);
  window.addEventListener('load', () => { syncAll(); setTimeout(syncAll, 1500); });

  /* ---------- Sticky header shadow ---------- */
  const onScroll = () => document.documentElement.classList.toggle('sl-scrolled', window.scrollY > 40);
  window.addEventListener('scroll', onScroll, { passive: true }); onScroll();
})();
